import { apiClient } from "./client";

export const TERMINAL_BACKTEST_STATES = new Set(["completed", "failed", "cancelled", "canceled"]);
export const BACKTEST_POLL_INTERVAL_MS = 15000;
const numberOrNull = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
const percentToDecimal = (value) => {
  const number = numberOrNull(value);
  return number === null ? null : number / 100;
};

export function normalizeBacktestResponse(payload, strategy = {}) {
  const backtest = payload?.backtest ?? payload;
  const rawMetrics = backtest?.metrics_json ?? backtest?.metrics ?? {};
  const submittedStrategy = backtest?.strategy_json ?? strategy ?? {};
  const deterministicSummary = backtest?.strategy_summary ?? submittedStrategy?.strategy_summary ?? null;
  const editorState = backtest?.editor_state_json ?? submittedStrategy?.editor_state ?? null;
  const settings = submittedStrategy?.settings ?? {};
  const resultData = backtest?.result_data ?? {};
  const rawBenchmarks = resultData?.benchmarks ?? backtest?.benchmarks ?? {};
  const rawEquity = backtest?.result_data?.equity_curve ?? backtest?.equity_curve ?? backtest?.equity ?? payload?.equity_curve ?? payload?.equity;
  const hasCurve = Array.isArray(rawEquity) && rawEquity.length > 0;
  const equity = hasCurve
    ? rawEquity.map((point, index) => ({
        label: point.label ?? point.date ?? point.timestamp ?? String(index + 1),
        value: numberOrNull(point.total_equity ?? point.value ?? point.equity ?? point.close),
        cash: numberOrNull(point.cash), investedValue: numberOrNull(point.invested_value),
        drawdownPercent: numberOrNull(point.drawdown_percent), positions: numberOrNull(point.positions)
      })).filter((point) => point.value !== null)
    : [
        { label: "Start", value: numberOrNull(rawMetrics.start_equity) },
        { label: "End", value: numberOrNull(rawMetrics.end_equity) }
      ].filter((point) => point.value !== null);
  const metric = (key, ...fallbackKeys) => numberOrNull([key, ...fallbackKeys].map((name) => rawMetrics?.[name]).find((value) => value !== null && value !== undefined));
  return {
    id: backtest?.id,
    displayName: backtest?.display_name ?? "Backtest",
    status: String(backtest?.status ?? payload?.status ?? "running").toLowerCase(),
    generatedAt: backtest?.finished_at ? new Date(backtest.finished_at).toLocaleString() : new Date().toLocaleString(),
    strategySummary: {
      initialCapital: metric("start_equity") ?? numberOrNull(settings.initial_capital),
      targetPositions: numberOrNull(settings.target_positions ?? settings.max_positions),
      rebalanceDays: numberOrNull(settings.rebalance_days),
      conditionCount: Array.isArray(submittedStrategy?.paths) ? submittedStrategy.paths.reduce((sum, path) => sum + (path.conditions?.length ?? 0), 0) : null,
      rankingVariable: submittedStrategy?.ranking?.variable ?? null,
      rankingDirection: submittedStrategy?.ranking?.direction ?? null
    },
    metrics: {
      startEquity: metric("start_equity"), finalValue: metric("end_equity"),
      grossProfit: metric("gross_profit"), netProfit: metric("net_profit"),
      totalReturn: percentToDecimal(rawMetrics.total_return_percent),
      cagr: percentToDecimal(rawMetrics.cagr_percent),
      sharpe: metric("sharpe_ratio", "sharpe_rough"), sortino: metric("sortino_ratio"), calmar: metric("calmar_ratio"),
      maxDrawdown: percentToDecimal(rawMetrics.max_drawdown_percent),
      maxDrawdownStartDate: rawMetrics.max_drawdown_start_date ?? null,
      maxDrawdownBottomDate: rawMetrics.max_drawdown_bottom_date ?? null,
      maxDrawdownRecoveryDate: rawMetrics.max_drawdown_recovery_date ?? null,
      volatility: percentToDecimal(rawMetrics.annualized_volatility ?? rawMetrics.volatility_percent),
      downsideDeviation: percentToDecimal(rawMetrics.downside_deviation),
      winRate: percentToDecimal(rawMetrics.win_rate_percent),
      trades: metric("trade_count") ?? numberOrNull(backtest?.trade_count), winningTrades: metric("winning_trade_count"), losingTrades: metric("losing_trade_count"),
      profitFactor: metric("profit_factor"), averageTradeReturn: percentToDecimal(rawMetrics.average_trade_return), medianTradeReturn: percentToDecimal(rawMetrics.median_trade_return),
      bestTradeReturn: percentToDecimal(rawMetrics.best_trade_return), worstTradeReturn: percentToDecimal(rawMetrics.worst_trade_return), averageWinningTrade: percentToDecimal(rawMetrics.average_winning_trade), averageLosingTrade: percentToDecimal(rawMetrics.average_losing_trade),
      payoffRatio: metric("payoff_ratio"), expectedReturnPerTrade: percentToDecimal(rawMetrics.expected_return_per_trade), maximumConsecutiveWins: metric("maximum_consecutive_wins"), maximumConsecutiveLosses: metric("maximum_consecutive_losses"),
      averageHoldingPeriod: metric("average_holding_period"), minimumHoldingPeriod: metric("minimum_holding_period"), maximumHoldingPeriod: metric("maximum_holding_period"),
      portfolioTurnover: percentToDecimal(rawMetrics.portfolio_turnover), rebalances: metric("rebalance_count"), rebalanceDates: rawMetrics.rebalance_dates ?? [],
      transactionCosts: metric("transaction_costs"), slippageCosts: metric("slippage_costs"), averageNumberOfPositions: metric("average_number_of_positions"), maximumSimultaneousPositions: metric("maximum_simultaneous_positions"),
      averageCashAllocation: percentToDecimal(rawMetrics.average_cash_allocation), averageInvestedAllocation: percentToDecimal(rawMetrics.average_invested_allocation), percentageOfTimeInvested: percentToDecimal(rawMetrics.percentage_of_time_invested), largestPositionConcentration: percentToDecimal(rawMetrics.largest_position_concentration),
      rejectedSignalCount: metric("rejected_signal_count"), unfilledOrderCount: metric("unfilled_order_count")
    },
    equity,
    equityIsSummary: !hasCurve,
    equityPointCount: numberOrNull(resultData.equity_curve_total ?? backtest?.equity_point_count) ?? equity.length,
    equitySampled: Boolean(resultData.equity_curve_sampled),
    trades: Array.isArray(resultData.trades) ? resultData.trades : [],
    tradesTotal: numberOrNull(resultData.trades_total) ?? 0,
    tradesReturned: numberOrNull(resultData.trades_returned) ?? 0,
    holdings: Array.isArray(resultData.portfolio_holdings_by_date) ? resultData.portfolio_holdings_by_date : [],
    holdingsDatesTotal: numberOrNull(resultData.holdings_dates_total) ?? 0,
    holdingsDatesSampled: Boolean(resultData.holdings_dates_sampled),
    benchmarks: {
      source: rawBenchmarks?.source ?? null,
      priceField: rawBenchmarks?.price_field ?? null,
      startDate: rawBenchmarks?.start_date ?? null,
      endDate: rawBenchmarks?.end_date ?? null,
      series: Object.fromEntries(Object.entries(rawBenchmarks?.series ?? {}).map(([symbol, points]) => [
        symbol,
        Array.isArray(points) ? points.map((point) => ({
          label: point.date ?? point.label ?? point.timestamp,
          value: numberOrNull(point.close ?? point.adjusted_close ?? point.value)
        })).filter((point) => point.value !== null) : []
      ]))
    },
    resultSchemaVersion: numberOrNull(backtest?.result_schema_version ?? resultData.result_schema_version),
    strategy: submittedStrategy,
    deterministicSummary,
    editorState,
    rawMetrics
  };
}
export function createIdempotencyKey() {
  if (!window.crypto?.randomUUID) throw new Error("Secure request identifiers are not supported by this browser.");
  return window.crypto.randomUUID();
}
export async function submitBacktest({ strategy, strategySummary, editorState, startDate, endDate, chunkSize = 100000, warmupDays = 200, idempotencyKey }) {
  const response = await apiClient.post("/api/lab/backtests", {
    strategy_json: strategy, start_date: startDate, end_date: endDate,
    strategy_summary: strategySummary,
    editor_state_json: editorState,
    chunk_size: chunkSize, warmup_days: warmupDays
  }, { headers: { "Idempotency-Key": idempotencyKey } });
  return response.data;
}
export async function getBacktestCredits({ signal } = {}) {
  return (await apiClient.get("/api/lab/backtests/credits", { signal })).data;
}
export async function getBacktest(backtestId, pollUrl, { holdingsDates, equityPoints = 160 } = {}) {
  const safePollUrl = typeof pollUrl === "string" && pollUrl.startsWith("/api/lab/backtests/")
    ? pollUrl
    : `/api/lab/backtests/${encodeURIComponent(backtestId)}`;
  return (await apiClient.get(safePollUrl, {
    params: {
      equity_points: equityPoints,
      ...(holdingsDates ? { holdings_dates: holdingsDates } : {})
    }
  })).data;
}
export async function renameBacktest(backtestId, name) {
  return (await apiClient.patch(`/api/lab/backtests/${encodeURIComponent(backtestId)}`, { name })).data;
}
export async function deleteBacktest(backtestId) {
  await apiClient.delete(`/api/lab/backtests/${encodeURIComponent(backtestId)}`);
}
export async function getBacktests({ page = 1, limit = 20, status = "", signal } = {}) {
  const response = await apiClient.get("/api/lab/backtests", {
    params: { page, limit, ...(status ? { status } : {}) },
    signal
  });
  return response.data;
}
export function getBacktestErrorMessage(error) {
  if (!error.response) return "Network error. Check your connection and retry; the same submission key will be reused.";
  if (error.response.data?.error === "Backtest credits exhausted") return "Backtest credits exhausted.";
  return ({400:"The backtest request is invalid. Check the strategy and date range.",401:"Your session has expired. Please sign in again.",429:"Too many backtest requests. Please wait and retry.",502:"The backtest service is temporarily unavailable. Please retry shortly."})[error.response.status] || "The backtest could not be completed. Please try again.";
}
